import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { getInboxEmails, markEmailAsRead, EmailMessage, getMailFolders, MailFolder, getEmailDetails, downloadEmailAttachment } from '../api/emails';
import ClaimAssignmentModal from './ClaimAssignmentModal';
import './Inbox.css';

interface InboxProps {
  onEmailSelect?: (email: EmailMessage) => void;
  onAssignToClaim?: (email: EmailMessage, claimId: string) => void;
  onClaimUpdate?: (claimId: string) => void;
  onRefreshingChange?: (refreshing: boolean) => void;
  defaultTab?: string;
}

interface InboxTab {
  id: string;
  label: string;
  email?: string;
  icon: string;
  color: string;
}

const Inbox = forwardRef<any, InboxProps>(({ onEmailSelect, onAssignToClaim, onClaimUpdate, onRefreshingChange, defaultTab }, ref) => {
  const [emails, setEmails] = useState<EmailMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<EmailMessage | null>(null);
  const [currentFolder, setCurrentFolder] = useState('inbox');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState(defaultTab || 'personal');
  const [folders, setFolders] = useState<MailFolder[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [fullEmailDetails, setFullEmailDetails] = useState<any>(null);
  const [loadingEmailDetails, setLoadingEmailDetails] = useState(false);

  const inboxTabs: InboxTab[] = [
    { id: 'personal', label: 'Personal', icon: '📧', color: '#2563eb' },
    { id: 'caf', label: 'CAF', email: 'caf@complexlaw.co.uk', icon: '🏢', color: '#16a34a' },
    { id: 'cancellations', label: 'Cancellations', email: 'cancellation@complexlaw.co.uk', icon: '❌', color: '#dc2626' }
  ];

  const ITEMS_PER_PAGE = 50;

  // Load folders when tab changes
  useEffect(() => {
    if (activeTab) {
      loadFolders();
    }
  }, [activeTab]);

  useEffect(() => {
    if (currentFolder) {
      loadEmails();
    }
  }, [currentFolder, page, activeTab]);

  const loadEmails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const currentTab = inboxTabs.find(tab => tab.id === activeTab);
      const targetEmail = currentTab?.email;
      
      const response = await getInboxEmails(
        currentFolder,
        page * ITEMS_PER_PAGE,
        ITEMS_PER_PAGE,
        searchQuery || undefined,
        targetEmail
      );
      
      if (response && response.success) {
        if (page === 0) {
          setEmails(response.emails || []);
        } else {
          setEmails(prev => [...prev, ...(response.emails || [])]);
        }
        setHasMore(response.hasMore || false);
      } else {
        throw new Error(response?.error || 'Failed to load emails');
      }
    } catch (err: any) {
      console.error('Failed to load emails:', err);
      setError(err.message || 'Failed to load emails');
    } finally {
      setLoading(false);
      setRefreshing(false);
      onRefreshingChange?.(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    onRefreshingChange?.(true);
    setPage(0);
    loadEmails();
  };

  const loadFolders = async () => {
    try {
      setFoldersLoading(true);
      const currentTab = inboxTabs.find(tab => tab.id === activeTab);
      const targetEmail = currentTab?.email;
      
      const response = await getMailFolders(targetEmail);
      
      if (response && response.success) {
        const fetchedFolders = response.folders || [];
        
        // Sort folders to ensure "Inbox" is always first
        const sortedFolders = [...fetchedFolders].sort((a, b) => {
          // Check if folder is inbox (by id or displayName)
          const aIsInbox = a.id.toLowerCase().includes('inbox') || a.displayName.toLowerCase() === 'inbox';
          const bIsInbox = b.id.toLowerCase().includes('inbox') || b.displayName.toLowerCase() === 'inbox';
          
          if (aIsInbox && !bIsInbox) return -1;
          if (!aIsInbox && bIsInbox) return 1;
          
          // Then sort by standard folder order
          const standardOrder = ['sentitems', 'sent', 'drafts', 'deleted', 'junk', 'archive'];
          const aIndex = standardOrder.findIndex(s => a.id.toLowerCase().includes(s) || a.displayName.toLowerCase().includes(s));
          const bIndex = standardOrder.findIndex(s => b.id.toLowerCase().includes(s) || b.displayName.toLowerCase().includes(s));
          
          if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
          if (aIndex !== -1) return -1;
          if (bIndex !== -1) return 1;
          
          // Finally, sort alphabetically
          return a.displayName.localeCompare(b.displayName);
        })
        
        setFolders(sortedFolders);
        
        // Set default folder if current folder is not in the list
        if (!sortedFolders.find(f => f.id.toLowerCase() === currentFolder.toLowerCase())) {
          const inboxFolder = sortedFolders.find(f => f.id.toLowerCase().includes('inbox') || f.displayName.toLowerCase() === 'inbox');
          if (inboxFolder) {
            setCurrentFolder(inboxFolder.id);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load folders:', err);
    } finally {
      setFoldersLoading(false);
    }
  };

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setPage(0);
    setSelectedEmail(null);
    setSearchQuery('');
    // Reset to inbox folder when switching tabs to avoid cross-mailbox folder ID issues
    setCurrentFolder('inbox');
  };

  // Expose handleRefresh method to parent components
  useImperativeHandle(ref, () => ({
    handleRefresh
  }));

  const handleFolderChange = (folder: string) => {
    setCurrentFolder(folder);
    setPage(0);
    setSelectedEmail(null);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
    loadEmails();
  };

  const handleEmailClick = async (email: EmailMessage) => {
    setSelectedEmail(email);
    setFullEmailDetails(null);
    setLoadingEmailDetails(true);
    
    // Fetch full email details
    try {
      const currentTab = inboxTabs.find(tab => tab.id === activeTab);
      const targetEmail = currentTab?.email;
      const details = await getEmailDetails(email.id, targetEmail);
      setFullEmailDetails(details);
    } catch (err) {
      console.error('Failed to load email details:', err);
    } finally {
      setLoadingEmailDetails(false);
    }
    
    if (!email.isRead) {
      try {
        await markEmailAsRead(email.id);
        setEmails(prev => prev.map(e => 
          e.id === email.id ? { ...e, isRead: true } : e
        ));
      } catch (err) {
        console.error('Failed to mark email as read:', err);
      }
    }
    
    if (onEmailSelect) {
      onEmailSelect(email);
    }
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  const handleAttachmentDownload = async (attachment: any) => {
    try {
      if (!selectedEmail) return;
      
      const currentTab = inboxTabs.find(tab => tab.id === activeTab);
      const targetEmail = currentTab?.email;
      
      await downloadEmailAttachment(selectedEmail.id, attachment.id, attachment.name, targetEmail);
    } catch (error) {
      console.error('Failed to download attachment:', error);
      alert('Failed to download attachment. Please try again.');
    }
  };

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningEmail, setAssigningEmail] = useState<EmailMessage | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-GB', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    }
  };

  if (loading && emails.length === 0) {
    return <div className="inbox-loading">Loading emails...</div>;
  }

  const isSharedInbox = activeTab !== 'personal';
  const currentTab = inboxTabs.find(tab => tab.id === activeTab);

  return (
    <div className="inbox-container">
      <div className="inbox-tabs">
        {inboxTabs.map(tab => (
          <button
            key={tab.id}
            className={`inbox-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => handleTabChange(tab.id)}
            style={{
              '--tab-color': tab.color
            } as React.CSSProperties}
          >
            <div className="tab-icon">{tab.icon}</div>
            <div>
              <div className="tab-label">{tab.label}</div>
              {tab.email && <div className="tab-email">{tab.email}</div>}
            </div>
          </button>
        ))}
      </div>
      {isSharedInbox && (
        <div className="shared-inbox-notice">
          <span className="notice-icon">ℹ️</span>
          <span>Viewing shared inbox - Email sending is disabled</span>
        </div>
      )}
      <div className="inbox-content">
        <div className="inbox-sidebar">
        <div className="folder-list">
          {foldersLoading ? (
            <div className="folder-loading">
              Loading folders...
            </div>
          ) : folders.length === 0 ? (
            <div className="folder-empty">
              No folders found
            </div>
          ) : (
            folders.map(folder => {
              const folderIcons: { [key: string]: string } = {
                'inbox': '📨',
                'sent': '✉️',
                'draft': '📋',
                'deleted': '🗑️',
                'junk': '📧',
                'archive': '📁',
                'outbox': '📤'
              };
              
              let icon = '📂'; // default folder icon
              const folderNameLower = folder.displayName.toLowerCase();
              const folderIdLower = folder.id.toLowerCase();
              
              for (const [key, value] of Object.entries(folderIcons)) {
                if (folderNameLower.includes(key) || folderIdLower.includes(key)) {
                  icon = value;
                  break;
                }
              }
              
              return (
                <div 
                  key={folder.id}
                  className={`folder-item ${currentFolder === folder.id ? 'active' : ''}`}
                  onClick={() => handleFolderChange(folder.id)}
                  title={`${folder.displayName} (${folder.unreadItemCount} unread)`}
                >
                  <span className="folder-icon">{icon}</span>
                  <span className="folder-name">{folder.displayName}</span>
                  {folder.unreadItemCount > 0 && (
                    <span className="folder-unread-count">{folder.unreadItemCount}</span>
                  )}
                </div>
              );
            })
          )}
        </div>
        </div>

      <div className="inbox-main">
        <div className="inbox-header">
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              placeholder="Search emails..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <button type="submit" className="search-button">Search</button>
          </form>
        </div>

        {error && (
          <div className="error-message">{error}</div>
        )}

        <div className="email-list">
          {emails.length === 0 ? (
            <div className="no-emails">
              <div>No emails found in {currentFolder} for {currentTab?.label || 'this'} inbox</div>
              <div style={{ fontSize: '13px', marginTop: '8px' }}>Try refreshing or checking another folder</div>
            </div>
          ) : (
            <>
              {emails.map(email => (
                <div
                  key={email.id}
                  className={`email-item ${!email.isRead ? 'unread' : ''} ${selectedEmail?.id === email.id ? 'selected' : ''}`}
                  onClick={() => handleEmailClick(email)}
                >
                  <div className="email-item-left">
                    <div className="email-from">
                      {email.from?.emailAddress?.name || email.from?.emailAddress?.address || 'Unknown sender'}
                    </div>
                    <div className="email-subject">
                      {email.subject || '(No subject)'}
                    </div>
                    <div className="email-preview">
                      {email.bodyPreview}
                    </div>
                  </div>
                  <div className="email-item-right">
                    <div className="email-date">
                      {formatDate(email.receivedDateTime)}
                    </div>
                    {email.hasAttachments && (
                      <div className="email-attachment-indicator">📎</div>
                    )}
                  </div>
                </div>
              ))}
              
              {hasMore && (
                <div className="load-more">
                  <button 
                    onClick={handleLoadMore} 
                    disabled={loading}
                    className="load-more-button"
                  >
                    {loading ? 'Loading...' : 'Load More'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      </div>

      {selectedEmail && (
        <div className="email-preview-pane">
          <div className="preview-header">
            <h3>{selectedEmail.subject || '(No subject)'}</h3>
            <button 
              className="close-preview"
              onClick={() => {
                setSelectedEmail(null);
                setFullEmailDetails(null);
              }}
            >
              ×
            </button>
          </div>
          <div className="preview-meta">
            <div>
              <strong>From:</strong> {selectedEmail.from.emailAddress.name || selectedEmail.from.emailAddress.address}
            </div>
            <div>
              <strong>To:</strong> {selectedEmail.toRecipients.map(r => r.emailAddress.address).join(', ')}
            </div>
            <div>
              <strong>Date:</strong> {new Date(selectedEmail.receivedDateTime).toLocaleString()}
            </div>
          </div>
          <div className="preview-body">
            {loadingEmailDetails ? (
              <div className="loading-email-details">Loading full email content...</div>
            ) : fullEmailDetails ? (
              <>
                <div className="email-body-content">
                  {fullEmailDetails.body?.contentType === 'html' ? (
                    <div 
                      className="email-html-content"
                      dangerouslySetInnerHTML={{ __html: fullEmailDetails.body.content }}
                    />
                  ) : (
                    <pre className="email-text-content">{fullEmailDetails.body?.content || selectedEmail.bodyPreview}</pre>
                  )}
                </div>
                
                {fullEmailDetails.attachments && fullEmailDetails.attachments.length > 0 && (
                  <div className="email-attachments">
                    <h4>Attachments ({fullEmailDetails.attachments.length})</h4>
                    <div className="attachments-list">
                      {fullEmailDetails.attachments.map((attachment: any, index: number) => (
                        <div 
                          key={index} 
                          className="attachment-item clickable"
                          onClick={() => handleAttachmentDownload(attachment)}
                          title="Click to download"
                        >
                          <span className="attachment-icon">📎</span>
                          <span className="attachment-name">{attachment.name}</span>
                          <span className="attachment-size">
                            {attachment.size ? `(${(attachment.size / 1024).toFixed(1)} KB)` : ''}
                          </span>
                          <span className="attachment-download-icon">⬇️</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p>{selectedEmail.bodyPreview}</p>
            )}
            
            <div className="preview-actions">
              <button 
                className="assign-to-claim-button"
                onClick={() => {
                  setAssigningEmail(selectedEmail);
                  setShowAssignModal(true);
                }}
              >
                <span style={{ fontSize: '16px' }}>🔗</span> Assign to Claim
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Claim Assignment Modal */}
      {showAssignModal && assigningEmail && (
        <ClaimAssignmentModal
          isOpen={showAssignModal}
          onClose={() => {
            setShowAssignModal(false);
            setAssigningEmail(null);
          }}
          emailId={assigningEmail.id}
          emailSubject={assigningEmail.subject || '(No subject)'}
          onSuccess={(claimId) => {
            alert(`Email successfully assigned to claim ${claimId}`);
            setShowAssignModal(false);
            setAssigningEmail(null);
          }}
          onClaimUpdate={onClaimUpdate}
        />
      )}
    </div>
  );
});

Inbox.displayName = 'Inbox';

export default Inbox;
